import React, { useState, useMemo } from 'react';
import { useAvailability } from '../hooks/useAvailability';
import { useRequests } from '../hooks/useRequests';
import { useSchools } from '../hooks/useSchools';
import Calendar from '../components/Calendar';

const DAYS_NL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

export default function AdminCalendarPage() {
  const { availability, isConnected, isLoading, updateAvailability, bulkUpdateAvailability } = useAvailability();
  const { requests, updateRequestStatus, deleteRequest } = useRequests();
  const { schools, filter: schoolFilter, setFilter: setSchoolFilter, pendingCount, approveSchool, deleteSchool } = useSchools();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<'available' | 'unavailable' | 'partial'>('available');
  const [editNotes, setEditNotes] = useState('');
  const [editTimeSlots, setEditTimeSlots] = useState([{ start: '09:00', end: '17:00' }]);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Multi-select state
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  
  // Demo request state
  const [showDemoPanel, setShowDemoPanel] = useState(false);
  const [demoMessage, setDemoMessage] = useState('');

  // Current displayed month
  const [displayedMonth, setDisplayedMonth] = useState<{ year: number; month: number }>({ 
    year: new Date().getFullYear(), 
    month: new Date().getMonth() 
  });
  const [isSettingMonth, setIsSettingMonth] = useState(false);

  // Set entire month as available
  const handleSetMonthAvailable = async () => {
    const { year, month } = displayedMonth;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dates: string[] = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      dates.push(dateStr);
    }
    
    setIsSettingMonth(true);
    setMessage('');
    
    try {
      await bulkUpdateAvailability(
        dates,
        'available',
        '',
        [{ start: '09:00', end: '17:00' }]
      );
      setMessage(`✅ ${MONTHS_NL[month]} ${year} is nu volledig beschikbaar!`);
    } catch (error: any) {
      setMessage(`❌ ${error.message}`);
    } finally {
      setIsSettingMonth(false);
    }
  };

  // When selecting a date, load its data
  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    const data = availability[date];
    if (data) {
      setEditStatus(data.status);
      setEditNotes(data.notes || '');
      setEditTimeSlots(data.timeSlots || [{ start: '09:00', end: '17:00' }]);
    } else {
      setEditStatus('available');
      setEditNotes('');
      setEditTimeSlots([{ start: '09:00', end: '17:00' }]);
    }
    setMessage('');
  };

  const handleSave = async () => {
    if (multiSelectMode) {
      // Bulk save for multiple dates
      if (selectedDates.length === 0) return;
      
      setIsSaving(true);
      setMessage('');
      
      try {
        await bulkUpdateAvailability(
          selectedDates,
          editStatus,
          editNotes,
          editStatus !== 'unavailable' ? editTimeSlots : undefined
        );
        setMessage(`✅ ${selectedDates.length} dagen opgeslagen!`);
        setSelectedDates([]);
      } catch (error: any) {
        setMessage(`❌ ${error.message}`);
      } finally {
        setIsSaving(false);
      }
    } else {
      // Single date save
      if (!selectedDate) return;
      
      setIsSaving(true);
      setMessage('');
      
      try {
        await updateAvailability(
          selectedDate,
          editStatus,
          editNotes,
          editStatus !== 'unavailable' ? editTimeSlots : undefined
        );
        setMessage('✅ Opgeslagen!');
      } catch (error: any) {
        setMessage(`❌ ${error.message}`);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const toggleMultiSelectMode = () => {
    if (multiSelectMode) {
      // Switch back to single select
      setMultiSelectMode(false);
      setSelectedDates([]);
    } else {
      // Switch to multi-select
      setMultiSelectMode(true);
      setSelectedDate(null);
      // Reset edit values to defaults
      setEditStatus('available');
      setEditNotes('');
      setEditTimeSlots([{ start: '09:00', end: '17:00' }]);
    }
    setMessage('');
  };

  const selectedDateFormatted = useMemo(() => {
    if (!selectedDate) return '';
    const d = new Date(selectedDate);
    return `${DAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]} ${d.getFullYear()}`;
  }, [selectedDate]);

  const addTimeSlot = () => {
    setEditTimeSlots([...editTimeSlots, { start: '09:00', end: '17:00' }]);
  };

  const removeTimeSlot = (index: number) => {
    setEditTimeSlots(editTimeSlots.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index: number, field: 'start' | 'end', value: string) => {
    const updated = [...editTimeSlots];
    updated[index][field] = value;
    setEditTimeSlots(updated);
  };

  // Demo function to create a test request
  const createDemoRequest = async () => {
    setDemoMessage('');
    
    // Get available dates for demo
    const availableDates = Object.keys(availability).filter(date => {
      const avail = availability[date];
      return avail && (avail.status === 'available' || avail.status === 'partial');
    }).slice(0, 2); // Take first 2 available dates
    
    if (availableDates.length === 0) {
      setDemoMessage('❌ Geen beschikbare dagen om te testen. Markeer eerst dagen als beschikbaar.');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_URL}/api/requests/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dates: availableDates,
          schoolName: 'Demo School',
          email: 'deflexibeleschakel@gmail.com',
          notes: 'Dit is een demo aanvraag om te testen of het systeem werkt.'
        })
      });
      
      if (response.ok) {
        setDemoMessage('✅ Demo aanvraag verstuurd! Je ziet hem nu in de lijst hieronder.');
      } else {
        const error = await response.json();
        setDemoMessage(`❌ ${error.error || 'Fout bij versturen'}`);
      }
    } catch (error: any) {
      setDemoMessage(`❌ ${error.message}`);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${MONTHS_NL[d.getMonth()].substring(0, 3)}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#e65100', icon: '⏳', text: 'In behandeling' };
      case 'accepted':
        return { bg: '#e8f5e9', color: '#2e7d32', icon: '✅', text: 'Geaccepteerd' };
      case 'declined':
        return { bg: '#ffebee', color: '#c62828', icon: '❌', text: 'Afgewezen' };
      default:
        return { bg: '#f5f5f5', color: '#666', icon: '❓', text: status };
    }
  };

  const handleUpdateStatus = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      await updateRequestStatus(requestId, status);
    } catch (error: any) {
      alert(`Fout: ${error.message}`);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!window.confirm('Weet je zeker dat je deze aanvraag wilt verwijderen?')) return;
    try {
      await deleteRequest(requestId);
    } catch (error: any) {
      alert(`Fout: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="container" style={{ textAlign: 'center', marginTop: '50px' }}>
        Beschikbaarheid laden...
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Beheer je Beschikbaarheid</h2>
        <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
          <button
            className={`btn ${multiSelectMode ? 'btn-secondary' : 'btn-outline'}`}
            onClick={toggleMultiSelectMode}
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}
          >
            {multiSelectMode ? '✓ Meerdere dagen selecteren' : '📅 Meerdere dagen selecteren'}
          </button>
          <div className={`realtime-indicator ${isConnected ? '' : 'disconnected'}`}>
            <div className="realtime-dot"></div>
            {isConnected ? 'Live verbonden' : 'Verbinding verbroken'}
          </div>
        </div>
      </div>

      {multiSelectMode && (
        <div style={{ 
          background: '#e3f2fd', 
          padding: '12px 16px', 
          borderRadius: 8, 
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            <strong>Multi-select modus:</strong> Klik op meerdere dagen om ze te selecteren. 
            {selectedDates.length > 0 && ` (${selectedDates.length} dagen geselecteerd)`}
          </span>
          {selectedDates.length > 0 && (
            <button
              onClick={() => setSelectedDates([])}
              style={{ background: 'transparent', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Selectie wissen
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
        <div>
          <Calendar
            availability={availability}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            multiSelect={multiSelectMode}
            selectedDates={selectedDates}
            onMultiSelect={setSelectedDates}
            onMonthChange={(year, month) => setDisplayedMonth({ year, month })}
          />
          
          <div className="legend">
            <div className="legend-item">
              <div className="legend-dot available"></div>
              Beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot unavailable"></div>
              Niet beschikbaar
            </div>
            <div className="legend-item">
              <div className="legend-dot partial"></div>
              Gedeeltelijk
            </div>
          </div>

          {/* Set entire month available button */}
          <button
            onClick={handleSetMonthAvailable}
            disabled={isSettingMonth}
            style={{
              marginTop: 15,
              width: '100%',
              padding: '12px 20px',
              background: isSettingMonth ? '#ccc' : '#e8f5e9',
              border: '2px solid #4caf50',
              borderRadius: 8,
              cursor: isSettingMonth ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              color: '#2e7d32',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            📅 {isSettingMonth ? 'Bezig...' : `Hele ${MONTHS_NL[displayedMonth.month]} beschikbaar zetten`}
          </button>
        </div>

        {/* Multi-select edit panel */}
        {multiSelectMode && (
          <div className="day-detail">
            <h3>
              {selectedDates.length === 0 
                ? 'Selecteer dagen' 
                : `${selectedDates.length} dag${selectedDates.length === 1 ? '' : 'en'} geselecteerd`}
            </h3>

            {selectedDates.length > 0 && (
              <>
                <div className="status-buttons">
                  <button
                    className={`status-btn ${editStatus === 'available' ? 'available' : ''}`}
                    onClick={() => setEditStatus('available')}
                  >
                    ✅ Beschikbaar
                  </button>
                  <button
                    className={`status-btn ${editStatus === 'partial' ? 'active' : ''}`}
                    onClick={() => setEditStatus('partial')}
                  >
                    🕐 Gedeeltelijk
                  </button>
                  <button
                    className={`status-btn ${editStatus === 'unavailable' ? 'unavailable' : ''}`}
                    onClick={() => setEditStatus('unavailable')}
                  >
                    ❌ Niet beschikbaar
                  </button>
                </div>

                {editStatus !== 'unavailable' && (
                  <div className="time-slots">
                    <label style={{ fontWeight: 500, marginBottom: 10, display: 'block' }}>
                      Beschikbare tijden:
                    </label>
                    {editTimeSlots.map((slot, index) => (
                      <div className="time-slot" key={index}>
                        <input
                          type="time"
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(index, 'start', e.target.value)}
                        />
                        <span>tot</span>
                        <input
                          type="time"
                          value={slot.end}
                          onChange={(e) => updateTimeSlot(index, 'end', e.target.value)}
                        />
                        {editTimeSlots.length > 1 && (
                          <button
                            onClick={() => removeTimeSlot(index)}
                            style={{ background: '#ffebee', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={addTimeSlot}
                      style={{ marginTop: 10, background: '#e3f2fd', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
                    >
                      + Tijdslot toevoegen
                    </button>
                  </div>
                )}

                <textarea
                  className="notes-input"
                  placeholder="Notities (optioneel)"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />

                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                  style={{ marginTop: 20 }}
                >
                  {isSaving ? 'Opslaan...' : `${selectedDates.length} dag${selectedDates.length === 1 ? '' : 'en'} opslaan`}
                </button>

                {message && (
                  <div style={{ marginTop: 15, textAlign: 'center' }}>{message}</div>
                )}
              </>
            )}

            {selectedDates.length === 0 && (
              <p style={{ color: '#666', marginTop: 20 }}>
                Klik op dagen in de kalender om ze te selecteren. Je kunt dan alle geselecteerde dagen tegelijk bewerken.
              </p>
            )}
          </div>
        )}

        {/* Single date edit panel */}
        {!multiSelectMode && selectedDate && (
          <div className="day-detail">
            <h3>{selectedDateFormatted}</h3>

            <div className="status-buttons">
              <button
                className={`status-btn ${editStatus === 'available' ? 'available' : ''}`}
                onClick={() => setEditStatus('available')}
              >
                ✅ Beschikbaar
              </button>
              <button
                className={`status-btn ${editStatus === 'partial' ? 'active' : ''}`}
                onClick={() => setEditStatus('partial')}
              >
                🕐 Gedeeltelijk
              </button>
              <button
                className={`status-btn ${editStatus === 'unavailable' ? 'unavailable' : ''}`}
                onClick={() => setEditStatus('unavailable')}
              >
                ❌ Niet beschikbaar
              </button>
            </div>

            {editStatus !== 'unavailable' && (
              <div className="time-slots">
                <label style={{ fontWeight: 500, marginBottom: 10, display: 'block' }}>
                  Beschikbare tijden:
                </label>
                {editTimeSlots.map((slot, index) => (
                  <div className="time-slot" key={index}>
                    <input
                      type="time"
                      value={slot.start}
                      onChange={(e) => updateTimeSlot(index, 'start', e.target.value)}
                    />
                    <span>tot</span>
                    <input
                      type="time"
                      value={slot.end}
                      onChange={(e) => updateTimeSlot(index, 'end', e.target.value)}
                    />
                    {editTimeSlots.length > 1 && (
                      <button
                        onClick={() => removeTimeSlot(index)}
                        style={{ background: '#ffebee', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addTimeSlot}
                  style={{ marginTop: 10, background: '#e3f2fd', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
                >
                  + Tijdslot toevoegen
                </button>
              </div>
            )}

            <textarea
              className="notes-input"
              placeholder="Notities (optioneel)"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
            />

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
              style={{ marginTop: 20 }}
            >
              {isSaving ? 'Opslaan...' : 'Opslaan'}
            </button>

            {message && (
              <div style={{ marginTop: 15, textAlign: 'center' }}>{message}</div>
            )}
          </div>
        )}

        {!multiSelectMode && !selectedDate && (
          <div className="day-detail" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
            <p>Selecteer een datum om de beschikbaarheid te bewerken</p>
          </div>
        )}
      </div>

      {/* Demo & Requests Section */}
      <div style={{ marginTop: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <h3>📬 Invalwerk Aanvragen</h3>
          <button
            className="btn btn-outline"
            onClick={() => setShowDemoPanel(!showDemoPanel)}
            style={{ padding: '8px 16px', fontSize: '0.9rem' }}
          >
            🧪 Demo testen
          </button>
        </div>

        {/* Demo Panel */}
        {showDemoPanel && (
          <div style={{ 
            background: '#f3e5f5', 
            padding: 20, 
            borderRadius: 12, 
            marginBottom: 20,
            border: '2px dashed #9c27b0'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#7b1fa2' }}>🧪 Demo Functie</h4>
            <p style={{ margin: '0 0 15px 0', color: '#666' }}>
              Test of het aanvraag-systeem werkt door een demo aanvraag te versturen.
              <br />
              <small>E-mail voor notificaties: <strong>deflexibeleschakel@gmail.com</strong></small>
            </p>
            <button
              className="btn btn-primary"
              onClick={createDemoRequest}
              style={{ background: '#9c27b0' }}
            >
              📨 Verstuur demo aanvraag
            </button>
            {demoMessage && (
              <div style={{ marginTop: 15 }}>{demoMessage}</div>
            )}
          </div>
        )}

        {/* Requests List */}
        {requests.length === 0 ? (
          <div style={{ 
            background: '#f5f5f5', 
            padding: 40, 
            borderRadius: 12, 
            textAlign: 'center',
            color: '#999'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 15 }}>📭</div>
            <p>Nog geen aanvragen ontvangen</p>
            <p style={{ fontSize: '0.9rem' }}>
              Wanneer scholen dagen aanvragen, verschijnen ze hier.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {requests.map((request) => {
              const badge = getStatusBadge(request.status);
              return (
                <div 
                  key={request.id}
                  style={{
                    background: 'white',
                    borderRadius: 12,
                    padding: 20,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <strong style={{ fontSize: '1.1rem' }}>{request.schoolName}</strong>
                        <span 
                          style={{
                            background: badge.bg,
                            color: badge.color,
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: '0.8rem',
                            fontWeight: 500
                          }}
                        >
                          {badge.icon} {badge.text}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                        {request.dates.sort().map(date => (
                          <span 
                            key={date}
                            style={{
                              background: '#e3f2fd',
                              padding: '4px 10px',
                              borderRadius: 12,
                              fontSize: '0.85rem'
                            }}
                          >
                            📅 {formatDate(date)}
                          </span>
                        ))}
                      </div>
                      
                      {request.notes && (
                        <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '0.9rem' }}>
                          📝 {request.notes}
                        </p>
                      )}
                      
                      <div style={{ color: '#999', fontSize: '0.85rem' }}>
                        Aangevraagd: {new Date(request.createdAt).toLocaleDateString('nl-NL', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleUpdateStatus(request.id, 'accepted')}
                            style={{
                              background: '#e8f5e9',
                              border: 'none',
                              padding: '10px 16px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#2e7d32',
                              fontWeight: 500
                            }}
                          >
                            ✅ Accepteren
                          </button>
                          <button
                            onClick={() => handleUpdateStatus(request.id, 'declined')}
                            style={{
                              background: '#ffebee',
                              border: 'none',
                              padding: '10px 16px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#c62828',
                              fontWeight: 500
                            }}
                          >
                            ❌ Afwijzen
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteRequest(request.id)}
                        style={{
                          background: '#f5f5f5',
                          border: 'none',
                          padding: '10px 12px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          color: '#666'
                        }}
                        title="Verwijderen"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Schools Management Section */}
      <div style={{ marginTop: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <h3>
            🏫 Geregistreerde Scholen
            {pendingCount > 0 && (
              <span style={{
                background: '#ff9800',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: '0.8rem',
                marginLeft: 10
              }}>
                {pendingCount} wachten op goedkeuring
              </span>
            )}
          </h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSchoolFilter('all')}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: schoolFilter === 'all' ? '#1976d2' : '#e0e0e0',
                color: schoolFilter === 'all' ? 'white' : '#333',
                fontWeight: 500
              }}
            >
              Alle
            </button>
            <button
              onClick={() => setSchoolFilter('pending')}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: schoolFilter === 'pending' ? '#ff9800' : '#e0e0e0',
                color: schoolFilter === 'pending' ? 'white' : '#333',
                fontWeight: 500
              }}
            >
              ⏳ Wachtend ({pendingCount})
            </button>
            <button
              onClick={() => setSchoolFilter('approved')}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                background: schoolFilter === 'approved' ? '#4caf50' : '#e0e0e0',
                color: schoolFilter === 'approved' ? 'white' : '#333',
                fontWeight: 500
              }}
            >
              ✅ Goedgekeurd
            </button>
          </div>
        </div>

        {schools.length === 0 ? (
          <div style={{ 
            background: '#f5f5f5', 
            padding: 40, 
            borderRadius: 12, 
            textAlign: 'center',
            color: '#999'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 15 }}>🏫</div>
            <p>{schoolFilter === 'all' ? 'Nog geen scholen geregistreerd' : 
                schoolFilter === 'pending' ? 'Geen scholen wachtend op goedkeuring' :
                'Geen goedgekeurde scholen'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {schools.map((school) => (
              <div 
                key={school.id}
                style={{
                  background: 'white',
                  borderRadius: 12,
                  padding: 20,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  borderLeft: school.approved ? '4px solid #4caf50' : '4px solid #ff9800'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <strong style={{ fontSize: '1.1rem' }}>{school.schoolName || 'Geen naam'}</strong>
                      <span 
                        style={{
                          background: school.approved ? '#e8f5e9' : '#fff3e0',
                          color: school.approved ? '#2e7d32' : '#e65100',
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}
                      >
                        {school.approved ? '✅ Goedgekeurd' : '⏳ Wachtend'}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 15, color: '#666', fontSize: '0.9rem' }}>
                      <span>📧 {school.email}</span>
                      {school.contactPerson && <span>👤 {school.contactPerson}</span>}
                      {school.phone && <span>📱 {school.phone}</span>}
                    </div>
                    
                    <div style={{ color: '#999', fontSize: '0.85rem', marginTop: 10 }}>
                      Geregistreerd: {new Date(school.createdAt).toLocaleDateString('nl-NL', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric'
                      })}
                      {school.lastLogin && (
                        <span> • Laatst ingelogd: {new Date(school.lastLogin).toLocaleDateString('nl-NL')}</span>
                      )}
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {!school.approved && (
                      <button
                        onClick={async () => {
                          try {
                            await approveSchool(school.id);
                          } catch (error: any) {
                            alert(`Fout: ${error.message}`);
                          }
                        }}
                        style={{
                          background: '#e8f5e9',
                          border: 'none',
                          padding: '10px 16px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          color: '#2e7d32',
                          fontWeight: 500
                        }}
                      >
                        ✅ Goedkeuren
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Weet je zeker dat je "${school.schoolName}" wilt verwijderen? Dit kan niet ongedaan gemaakt worden.`)) return;
                        try {
                          await deleteSchool(school.id);
                        } catch (error: any) {
                          alert(`Fout: ${error.message}`);
                        }
                      }}
                      style={{
                        background: '#f5f5f5',
                        border: 'none',
                        padding: '10px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        color: '#666'
                      }}
                      title="Verwijderen"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
